import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { isLoggedIn, customerName, useAuth } from "../hooks/useAuth";
import { useI18n } from "../i18n";
import LanguageSwitcher from "../components/general/LanguageSwitcher";
import Drawer from "../components/general/Drawer";

/** Adın baş hərflərindən avatar (məs. "Afik Tahirov" → "AT") */
const initials = (name) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "?";

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { t } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);

  const logged = isLoggedIn();
  const name = customerName();

  // Səhifə dəyişəndə menyu bağlansın
  useEffect(() => setMenuOpen(false), [location.pathname]);

  const goLogin = () => {
    sessionStorage.setItem("quizzo_redirect", window.location.pathname);
    setMenuOpen(false);
    navigate("/login");
  };

  const links = useMemo(
    () => [
      { to: "/stores", label: t("nav.stores"), icon: "🏪" },
      {
        to: logged ? "/subscription" : "/plans",
        label: logged ? t("nav.subscription") : t("nav.plans"),
        icon: "💳",
      },
      ...(logged ? [{ to: "/coupons", label: t("nav.coupons_short"), icon: "🎟" }] : []),
      { to: "/about", label: t("nav.about"), icon: "ℹ️" },
    ],
    [logged, t]
  );

  const desktopLink = ({ isActive }) =>
    `text-sm font-semibold px-3.5 py-2 rounded-xl transition whitespace-nowrap ${
      isActive
        ? "bg-white/20 text-white shadow-inner"
        : "text-white/80 hover:text-white hover:bg-white/10"
    }`;

  const drawerLink = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3.5 rounded-2xl font-semibold transition ${
      isActive
        ? "bg-white text-indigo-700 shadow-lg"
        : "text-white/90 hover:bg-white/10 active:bg-white/15"
    }`;

  return (
    <header className="sticky top-0 z-30 bg-indigo-700/40 backdrop-blur-xl border-b border-white/15 pt-[env(safe-area-inset-top)]">
      <div className="max-w-6xl mx-auto px-4 h-14 sm:h-16 flex items-center justify-between gap-3">
        <Link to="/stores" className="flex items-center gap-2 group shrink-0">
          <span className="text-2xl transition-transform group-active:scale-90">🎯</span>
          <span className="text-lg sm:text-xl font-extrabold tracking-tight text-white">
            Quizzo
          </span>
        </Link>

        {/* Masaüstü */}
        <nav className="hidden md:flex items-center gap-1.5">
          {links.map((link) => (
            <NavLink key={link.to} to={link.to} className={desktopLink}>
              <span className="mr-1">{link.icon}</span>
              {link.label}
            </NavLink>
          ))}

          <div className="mx-1">
            <LanguageSwitcher />
          </div>

          {logged ? (
            <button
              onClick={() => setMenuOpen(true)}
              className="flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 transition"
              aria-label={name || t("nav.account")}
            >
              <span className="w-8 h-8 rounded-full bg-white text-indigo-700 text-xs font-extrabold flex items-center justify-center">
                {initials(name)}
              </span>
              <span className="text-white text-sm font-semibold max-w-[7rem] truncate">
                {name || t("nav.account")}
              </span>
            </button>
          ) : (
            <button
              onClick={goLogin}
              className="bg-white text-indigo-600 hover:bg-indigo-50 text-sm font-bold px-4 py-2 rounded-xl shadow transition"
            >
              {t("nav.login")}
            </button>
          )}
        </nav>

        {/* Mobil */}
        <div className="flex md:hidden items-center gap-2">
          <LanguageSwitcher compact />
          <button
            onClick={() => setMenuOpen(true)}
            aria-label={t("nav.menu")}
            className="w-10 h-10 flex flex-col items-center justify-center gap-[5px] rounded-xl bg-white/15 active:bg-white/25 active:scale-95 transition"
          >
            <span className="block w-[18px] h-[2px] bg-white rounded-full" />
            <span className="block w-[18px] h-[2px] bg-white rounded-full" />
            <span className="block w-[18px] h-[2px] bg-white rounded-full" />
          </button>
        </div>
      </div>

      {/* Yandan açılan menyu */}
      <Drawer open={menuOpen} onClose={() => setMenuOpen(false)} labelledBy="drawer-title">
        <div className="flex items-center justify-between px-5 pt-[calc(env(safe-area-inset-top)+1rem)] pb-4">
          <span id="drawer-title" className="text-white font-extrabold text-lg">
            🎯 Quizzo
          </span>
          <button
            onClick={() => setMenuOpen(false)}
            aria-label={t("common.close")}
            className="w-9 h-9 rounded-xl bg-white/15 active:bg-white/25 text-white text-lg leading-none flex items-center justify-center transition"
          >
            ✕
          </button>
        </div>

        {/* İstifadəçi kartı */}
        <div className="px-5 pb-4">
          {logged ? (
            <div className="flex items-center gap-3 bg-white/10 rounded-2xl p-3 border border-white/10">
              <span className="w-11 h-11 rounded-full bg-white text-indigo-700 font-extrabold flex items-center justify-center shrink-0">
                {initials(name)}
              </span>
              <div className="min-w-0">
                <p className="text-white font-bold truncate">{name || t("nav.account")}</p>
                <p className="text-white/60 text-xs">{t("nav.welcome_back")}</p>
              </div>
            </div>
          ) : (
            <button
              onClick={goLogin}
              className="w-full bg-white text-indigo-700 font-bold py-3 rounded-2xl shadow active:scale-[0.98] transition"
            >
              {t("nav.login")}
            </button>
          )}
        </div>

        {/* Keçidlər */}
        <nav className="flex-1 overflow-y-auto px-3 space-y-1.5">
          {links.map((link) => (
            <NavLink key={link.to} to={link.to} className={drawerLink}>
              <span className="text-xl w-6 text-center">{link.icon}</span>
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-5 pb-[calc(env(safe-area-inset-bottom)+1.25rem)] space-y-3 border-t border-white/10">
          <LanguageSwitcher full />

          {logged && (
            <button
              onClick={logout}
              className="w-full text-center bg-white/10 hover:bg-white/20 text-white font-semibold py-3 rounded-2xl transition active:scale-[0.98]"
            >
              {t("nav.logout")}
            </button>
          )}
        </div>
      </Drawer>
    </header>
  );
};

export default Navbar;
